import { useEffect, useState } from "react";
import { Users, CalendarCheck, CalendarClock, Crown, Euro, Search, Loader2, Star, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white border border-taupe/20 rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-taupe-dark">
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="text-[10px] tracking-[0.16em] uppercase">{label}</span>
    </div>
    <p className="font-display text-3xl text-espresso mt-2">{value}</p>
    {sub && <p className="text-xs text-espresso/50 mt-1">{sub}</p>}
  </div>
);

export const ClientsAdmin = ({ q, setQ }) => {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/clients").then((r) => setClients(r.data.clients)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const filtered = (clients || []).filter(
    (c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.phone.replace(/\D/g, "").includes(q.replace(/\D/g, ""))
  );

  return (
    <div className="space-y-8" data-testid="admin-clients-tab">
      {!stats ? (
        <div className="flex items-center gap-3 text-sm text-espresso/55 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stats-grid">
          <StatCard icon={CalendarCheck} label={`RDV · ${stats.month_label}`} value={stats.month_appointments} testid="stat-month" />
          <StatCard icon={CalendarClock} label="RDV à venir" value={stats.upcoming} sub={stats.pending > 0 ? `${stats.pending} en attente` : null} testid="stat-upcoming" />
          <StatCard icon={Users} label="Clientes" value={stats.total_clients} testid="stat-clients" />
          <StatCard icon={Crown} label="Régulières" value={stats.regular_clients} sub="2+ visites confirmées" testid="stat-regulars" />
          <StatCard icon={Euro} label="CA estimé du mois" value={`${stats.month_revenue} €`} sub="RDV confirmés" testid="stat-revenue" />
        </div>
      )}

      {stats?.top_services?.length > 0 && (
        <div className="bg-white border border-taupe/20 rounded-2xl p-5" data-testid="top-services">
          <p className="text-[10px] tracking-[0.16em] uppercase text-taupe-dark mb-3">Prestations les plus demandées</p>
          <div className="flex flex-wrap gap-3">
            {stats.top_services.map((s, i) => (
              <span key={s.name} className="rounded-full bg-beige/70 px-4 py-2 text-sm text-espresso">
                <span className="font-display text-taupe-dark mr-1.5">{i + 1}.</span>
                {s.name} <span className="text-espresso/50">· {s.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h3 className="font-display text-2xl text-espresso">Répertoire clientes</h3>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-espresso/40" strokeWidth={1.5} />
            <input
              data-testid="clients-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom ou téléphone…"
              className="bg-white border border-taupe/30 rounded-full pl-10 pr-4 py-2.5 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300 w-64"
            />
          </div>
        </div>
        {!clients ? (
          <div className="flex items-center gap-3 text-sm text-espresso/55 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-espresso/45 py-8 text-center" data-testid="clients-empty">Aucune cliente trouvée.</p>
        ) : (
          <div className="space-y-2" data-testid="clients-list">
            {filtered.map((c, i) => (
              <div
                key={`${c.phone}-${i}`}
                data-testid={`client-row-${i}`}
                className="bg-white border border-taupe/20 rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4 justify-between"
              >
                <div className="flex items-center gap-4 min-w-[200px]">
                  <span className={`w-11 h-11 rounded-full flex items-center justify-center font-display text-lg shrink-0 ${c.regular ? "bg-taupe text-cream" : "bg-beige text-taupe-dark"}`}>
                    {c.name.charAt(0).toUpperCase()}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-espresso flex items-center gap-2">
                      {c.name}
                      {c.regular && (
                        <span className="rounded-full bg-gold/15 text-taupe-dark px-2.5 py-0.5 text-[10px] tracking-[0.08em] uppercase inline-flex items-center gap-1" data-testid={`regular-badge-${i}`}>
                          <Star className="w-3 h-3 fill-taupe-dark" strokeWidth={0} /> Régulière
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-espresso/50 mt-0.5">{c.phone}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-8 gap-y-1 text-xs text-espresso/60">
                  <span><span className="font-display text-lg text-espresso">{c.visits}</span> visite{c.visits > 1 ? "s" : ""}</span>
                  {c.favorite && <span className="hidden sm:block">Aime : <span className="text-espresso">{c.favorite}</span></span>}
                  <span className="hidden md:block">Dernière : {c.last}</span>
                  {c.next ? (
                    <span className="rounded-full bg-[#4A6B53]/10 text-[#4A6B53] px-3 py-1 inline-flex items-center gap-1.5">
                      <CalendarClock className="w-3 h-3" strokeWidth={1.5} /> Prochain : {c.next}
                    </span>
                  ) : (
                    <span className="text-espresso/35 inline-flex items-center gap-1.5">
                      <Hourglass className="w-3 h-3" strokeWidth={1.5} /> Aucun RDV à venir
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
